/**
 * Formats a name part with proper capitalization:
 * - First letter capitalized
 * - Rest lowercase
 * - Special handling for names with apostrophes (e.g., O'Neil -> O'Neil, not O'neil)
 */
export const formatNamePart = (namePart: string | null | undefined): string => {
  if (!namePart || typeof namePart !== 'string') {
    return '';
  }

  const trimmed = namePart.trim();
  if (trimmed.length === 0) {
    return '';
  }

  // Handle names with apostrophes (e.g., O'Neil, D'Angelo)
  if (trimmed.includes("'")) {
    return trimmed
      .split("'")
      .map(part => {
        if (part.length === 0) return part;
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join("'");
  }

  // Handle names with hyphens (e.g., Mary-Jane, Jean-Pierre)
  if (trimmed.includes("-")) {
    return trimmed
      .split("-")
      .map(part => {
        if (part.length === 0) return part;
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join("-");
  }

  // Regular name formatting
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

/**
 * Formats a full name in "Last, First Middle" format with proper capitalization
 */
export const formatPatientName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  middleName: string | null | undefined
): string => {
  const formattedFirst = formatNamePart(firstName);
  const formattedLast = formatNamePart(lastName);
  const formattedMiddle = formatNamePart(middleName);

  // Build name parts array starting with last name
  const nameParts: string[] = [];
  
  if (formattedLast) {
    nameParts.push(formattedLast);
  }

  // Add comma and space after last name if we have first or middle name
  if (formattedFirst || formattedMiddle) {
    if (nameParts.length > 0) {
      nameParts[0] = nameParts[0] + ',';
    }
  }

  // Add first name
  if (formattedFirst) {
    nameParts.push(formattedFirst);
  }

  // Add middle name
  if (formattedMiddle) {
    nameParts.push(formattedMiddle);
  }

  return nameParts.join(' ');
};

export const lowerFirstLetter = (str: string) => {
  return str.charAt(0).toLowerCase() + str.slice(1);
}
